import { BaseHeader, Grid, Move } from '@morph-mapper/ui';
import {
  Box,
  Group,
  Menu,
  Text,
  Button,
  Divider,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { useStore } from '@/store';
import { useLocation } from 'react-router';
import { FiSave } from 'react-icons/fi';
import {
  RiLayoutColumnFill,
  RiLayoutRowFill,
  RiTestTubeLine,
  RiQuestionLine,
} from 'react-icons/ri';
import {
  TbFileExport,
  TbRefresh,
  TbSettings,
  TbTemplate,
} from 'react-icons/tb';
import { useHeader } from './use-header';
import { LayoutDirection } from '@/types';
import {
  MdSaveAlt,
  MdFormatListBulleted,
  MdArrowBack,
  MdArrowForward,
} from 'react-icons/md';
import { t } from 'i18next';
import { DOCS_USER_BASE_URL } from '@/config';
import { useSetupStore } from '../../../setup/store';
import { useDomainSelect } from '../../../setup/organisms/domain-select/use-domain-select';
import { useFileSelect } from '../../../setup/organisms/file-select/use-file-select';
import { useNotify, useNavigateAway } from '@/hooks';
import { FileSelector } from './file-selector';
import { BootstrapProgress } from './bootstrap-progress';
import { PluginLogsMenu } from './plugin-logs-menu';
import { Reevaluation } from '../../organisms/reevaluate-modal';

const ClickableTitle = ({ title }: { title: string }) => {
  const { requestNavigateToDomain } = useNavigateAway();

  return (
    <Text
      sx={{
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
      onClick={requestNavigateToDomain}
    >
      {title}
    </Text>
  );
};

const MenuActions = () => {
  const saveDisabled = useStore(({ config: c }) => c.getIsSaveDisabled());
  const direction = useStore(({ ui }) => ui.layoutDirection);
  const [rebootstrapInProgress, setReevaluateOpen] = useStore(({ ai }) => [
    ai.rebootstrapInProgress,
    ai.setReevaluateOpen,
  ]);
  const {
    handleTestTemplate,
    handleSaveFile,
    handleExportTemplate,
    handleDeployTemplate,
    handleConfigureTemplate,
    setLayoutVertical,
    setLayoutHorizontal,
    handleNavigateToTemplateSelection,
    handleNavigateToDomainSelection,
  } = useHeader();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', zIndex: 100 }}>
      <Menu shadow="md" width={150} position="bottom-end">
        <Menu.Target>
          <Grid />
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>{t('menu.templates.label')}</Menu.Label>
          <Menu.Item
            disabled={saveDisabled}
            icon={<RiTestTubeLine />}
            onClick={handleTestTemplate}
          >
            {t('menu.templates.test')}
          </Menu.Item>
          <Menu.Item
            disabled={saveDisabled}
            icon={<FiSave />}
            onClick={handleSaveFile}
          >
            {t('menu.templates.save')}
          </Menu.Item>
          <Menu.Item
            disabled={saveDisabled}
            icon={<MdSaveAlt />}
            onClick={handleExportTemplate}
          >
            {t('menu.templates.localSave')}
          </Menu.Item>
          <Menu.Item
            disabled={saveDisabled}
            icon={<TbFileExport />}
            onClick={handleDeployTemplate}
          >
            {t('menu.templates.deploy')}
          </Menu.Item>
          <Menu.Item
            disabled={rebootstrapInProgress}
            icon={<TbRefresh />}
            onClick={() => setReevaluateOpen(true)}
          >
            {t('menu.templates.reevaluate')}
          </Menu.Item>

          <Menu.Label>{t('menu.layout.label')}</Menu.Label>
          {direction === LayoutDirection.Column ? (
            <Menu.Item icon={<RiLayoutRowFill />} onClick={setLayoutHorizontal}>
              {t('menu.layout.horizontal')}
            </Menu.Item>
          ) : (
            <Menu.Item
              icon={<RiLayoutColumnFill />}
              onClick={setLayoutVertical}
            >
              {t('menu.layout.vertical')}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      <Reevaluation />
    </Box>
  );
};

const HelpButton = () => {
  // Deliberately not using useHeader() here — it pulls in several other
  // hooks (test/deploy/save/navigate-away) that assume a template is
  // already loaded. This button renders on every screen, including
  // setup steps before any template exists, so it only needs the one
  // thing it actually does.
  const handleDocumentation = () => {
    window.open(DOCS_USER_BASE_URL, '_blank');
  };

  return (
    <Tooltip label={t('menu.documentation.help')}>
      <ActionIcon
        variant="default"
        onClick={handleDocumentation}
        aria-label={t('menu.documentation.help')}
      >
        <RiQuestionLine size="1rem" />
      </ActionIcon>
    </Tooltip>
  );
};

const RightActions = () => {
  const { handleConfigureTemplate, handleNavigateToTemplateSelection } =
    useHeader();
  const name = useStore(({ config: c }) => c.name);
  return (
    <>
      {name ? (
        <>
          <Group
            spacing="xs"
            noWrap
            sx={{
              flexShrink: 0,
              minWidth: 0,
              maxWidth: '220px',
              marginRight: 4,
            }}
          >
            <TbTemplate
              size={16}
              style={{ flexShrink: 0, color: 'var(--mantine-color-gray-5)' }}
            />
            <Text
              size="sm"
              weight={500}
              color="dimmed"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'default',
              }}
              title={name}
            >
              {name}
            </Text>
          </Group>
          <Divider orientation="vertical" />
        </>
      ) : null}
      <Text
        onClick={handleConfigureTemplate}
        sx={{
          cursor: 'pointer',
          color: 'black',
          display: 'flex',
          alignItems: 'center',
          gap: '0.35rem',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <TbSettings />
        {t('menu.templates.configure')}
      </Text>
      <Text
        onClick={handleNavigateToTemplateSelection}
        sx={{
          cursor: 'pointer',
          color: 'black',
          display: 'flex',
          alignItems: 'center',
          gap: '0.35rem',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <MdFormatListBulleted />
        {t('menu.navigate.templateSelection')}
      </Text>
      <HelpButton />
      <MenuActions />
    </>
  );
};

export const Header = () => {
  const [headerTitle, headerBack, headerRightAction] = useStore(({ ui }) => [
    ui.headerTitle,
    ui.headerBack,
    ui.headerRightAction,
  ]);
  const [id, textContent, isNavTemplateOpen, isNavDomainOpen, isUpdateDiff] =
    useStore(({ config: c }) => [
      c.id,
      c.textContent,
      c.isNavTemplateModalOpen,
      c.isNavDomainModalOpen,
      c.isUpdateDiff,
    ]);
  const location = useLocation();
  const isSetupRoute = location.pathname === '/';

  // Setup Next/Previous controls derived without rendering full step content
  const [step, setCurrentStep, templateId] = useSetupStore((s) => [
    s.currentStep,
    s.setCurrentStep,
    s.templateId,
  ]);

  const handlePrevious = () => setCurrentStep(step - 1);

  const DomainNavButtons = () => {
    const { progressSetup } = useDomainSelect();
    const domain = useSetupStore((s) => s.domain);
    return (
      <>
        <Button
          variant="default"
          onClick={handlePrevious}
          sx={{ visibility: step !== 0 ? 'visible' : 'hidden' }}
        >
          <MdArrowBack />
        </Button>
        <Button disabled={domain === undefined} onClick={progressSetup}>
          <MdArrowForward />
        </Button>
      </>
    );
  };

  const TemplateNavButtons = () => {
    const { warn } = useNotify();
    const onNext = () => {
      if (templateId === undefined) {
        warn(t('message.warning.selectTemplate'));
        return;
      }
      setCurrentStep(step + 1);
    };
    return (
      <>
        <Button
          variant="default"
          onClick={handlePrevious}
          sx={{ visibility: step !== 0 ? 'visible' : 'hidden' }}
        >
          <MdArrowBack />
        </Button>
        <Button disabled={templateId === undefined} onClick={onNext}>
          <MdArrowForward />
        </Button>
      </>
    );
  };

  const FileNavButtons = () => {
    const { progressSetup, disableNext } = useFileSelect();
    return (
      <>
        <Button
          variant="default"
          onClick={handlePrevious}
          sx={{ visibility: step !== 0 ? 'visible' : 'hidden' }}
        >
          <MdArrowBack />
        </Button>
        <Button disabled={disableNext} onClick={progressSetup}>
          <MdArrowForward />
        </Button>
      </>
    );
  };

  const PluginSettingsNavButtons = () => {
    const { progressSetup, disableNext } = useFileSelect();
    return (
      <>
        <Button
          variant="default"
          onClick={handlePrevious}
          sx={{ visibility: step !== 0 ? 'visible' : 'hidden' }}
        >
          <MdArrowBack />
        </Button>
        <Button disabled={disableNext} onClick={progressSetup}>
          <MdArrowForward />
        </Button>
      </>
    );
  };

  // Intentionally omit Plugin step header controls to avoid double-bootstrapping loops

  const isEditorRoute =
    location.pathname.includes('/entries') ||
    location.pathname.includes('/configure') ||
    location.pathname.includes('/graph') ||
    location.pathname.includes('/code') ||
    location.pathname.includes('/context');

  const showMenu = Boolean(
    id &&
    textContent &&
    isEditorRoute &&
    !isNavTemplateOpen &&
    !isNavDomainOpen,
  );

  return (
    <BaseHeader>
      <Group>
        <ClickableTitle title={'MorphMapper'} />
        {isSetupRoute ? (
          <Group>
            {step === 0 && <DomainNavButtons />}
            {step === 1 && <TemplateNavButtons />}
            {step === 2 && <FileNavButtons />}
            {step === 3 && <PluginSettingsNavButtons />}
          </Group>
        ) : null}
      </Group>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 12,
          minWidth: 0,
        }}
      >
        {showMenu && !isUpdateDiff ? <FileSelector /> : null}
        {showMenu ? <PluginLogsMenu /> : null}
        <BootstrapProgress />
      </Box>

      <Group>
        {headerRightAction ?? null}
        {/* In the editor view, HelpButton lives inside RightActions,
            positioned among the other actions per review feedback. Outside
            that view (e.g. setup screens) there's no RightActions to host
            it, so it renders here instead — it stays visible everywhere
            either way, just in one place at a time. */}
        {!showMenu && <HelpButton />}
        {showMenu ? <RightActions /> : null}
      </Group>
    </BaseHeader>
  );
};
